import React from "react";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    Cell,
    LabelList
} from "recharts";
import { formatNumber } from "../../../DataGuard/Utility/DataguardConstant";

export default function ApplicationBarChart({ data, isDashboardPopup = false }) {
    // Helper function to create relative scaling using log transformation
    const createRelativeScale = (values, minHeight = 10, maxHeight = 90) => {
        // Apply logarithmic transformation: log10(value + 1) to handle zeros
        const logValues = values.map(val => Math.log10((val || 0) + 1));

        // Find min and max of log values
        const minLog = Math.min(...logValues);
        const maxLog = Math.max(...logValues);

        // If all values are the same, return equal heights
        if (minLog === maxLog) {
            return logValues.map(() => (minHeight + maxHeight) / 2);
        }

        // Normalize to desired range, ensuring all bars are visible
        return logValues.map(logVal => {
            const normalized = (logVal - minLog) / (maxLog - minLog);
            return minHeight + (normalized * (maxHeight - minHeight));
        });
    };

    // Extract total activity counts (including alerts) for scaling
    const allTotalCounts = data.map(app => (app.activityCount || 0));

    // Scale based on TOTAL activities to ensure proportional bar heights
    const scaledTotalHeights = createRelativeScale(allTotalCounts, 25, 90); // Total bar height range

    // Transform data with proportional scaling
    const chartData = data.map((app, index) => {
        const alertCount = parseInt(app.alertCount) || 0;
        const accessCount = parseInt(app.activityCount) || 0;
        const totalHeight = accessCount === 0 ? 0 : scaledTotalHeights[index];
        const minAlertHeight = 5;
        
        // Calculate proportional heights
        let alertHeight = alertCount > 0 ? totalHeight * (alertCount / accessCount) : 0;
        let accessHeight = totalHeight - alertHeight;
        
        // Ensure minimum visible height for alerts when they exist
        if (alertCount > 0 && alertHeight < minAlertHeight) {
            alertHeight = minAlertHeight;
            accessHeight = Math.max(0, totalHeight - minAlertHeight);
        }
        
        return {
            name: app.appDetails,
            abbr: app.appDetails?.split(" ").map(word => word[0]).join("").toUpperCase() || "Unknown",
            alertCount: alertHeight,
            accessCount: accessHeight,
            originalAlertCount: alertCount,
            originalAccessCount: accessCount,
            accessCountWithoutAlert: accessCount - alertCount,
        };
    });

    const CustomTooltip = ({ active, payload }) => {
        if (!active || !payload?.length) return null;
        
        const data = payload[0].payload;
        return (
            <div className="application-chart-tooltip">
                <p className="tooltip-title">{data.name}</p>
                <p className="tooltip-alerts">No. of Alerts: {data.originalAlertCount.toLocaleString('en-US')}</p>
                <p className="tooltip-activities">Activities w/o Alerts: {data.accessCountWithoutAlert.toLocaleString('en-US')}</p>
                <p className="tooltip-total">Total Activities: {data.originalAccessCount.toLocaleString('en-US')}</p>
            </div>
        );
    };

    const CustomBarLabel = (fillColor, isBottomBar = false) => ({ x, y, width, height, value }) => {
        if (value === 0) return null; // Don't render label for zero values
        const isSmallBar = height < 20;
        
        // For small bottom bars, place label on the right side
        if (isSmallBar && isBottomBar) {
            return (
                <text
                    x={x + width + 5}
                    y={y + height / 2}
                    textAnchor="start"
                    dominantBaseline="middle"
                    fill="#000000"
                    fontSize={isDashboardPopup ? 12 : 10}
                    fontWeight={isDashboardPopup ? "bold" : "normal"}
                >
                    {formatNumber(value)}
                </text>
            );
        }
        
        // For small top bars or normal bars, use existing logic
        return (
            <text
                x={x + width / 2}
                y={isSmallBar ? y - 5 : y + height / 2}
                textAnchor="middle"
                dominantBaseline={isSmallBar ? "auto" : "middle"}
                fill={isSmallBar ? "#000000" : fillColor}
                fontSize={isDashboardPopup ? 12 : 10}
                fontWeight={isDashboardPopup ? "bold" : "normal"}
            >
                {formatNumber(value)}
            </text>
        );
    };

    const height = isDashboardPopup ? 650 : 155;
    const barWidth = isDashboardPopup ? 35 : 25;
    const className = `chart-container-sysguard${isDashboardPopup ? " dashboardPopup" : ""}`;

    // Check if data is available
    if (!data || data.length === 0) {
        return (
            <div>
                {isDashboardPopup && <h3>Applications</h3>}
                <div className="sys-guard-user-expanded-graph-container">
                    <div>No Data Available</div>
                </div>
            </div>
        );
    }

    return (
        <div className={className}>
            {isDashboardPopup && <h3>Applications</h3>}
            <ResponsiveContainer width="100%" height={height}>
                <BarChart
                    data={chartData}
                    barCategoryGap={8}
                >
                    <XAxis
                        dataKey={isDashboardPopup ? "name" : "abbr"}
                        interval={0}
                        axisLine={false}
                        tickLine={false}
                    />
                    <YAxis hide domain={[0, 100]} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="accessCount" stackId="a" fill="#8dc63f" barSize={barWidth} >
                        {chartData.map((entry, index) => (
                            <Cell
                                key={`access-${index}`}
                                radius={entry.originalAlertCount === 0 ? [10, 10, 10, 10] : [0, 0, 10, 10]}
                            />
                        ))}
                        <LabelList
                            dataKey="accessCountWithoutAlert"
                            content={CustomBarLabel("#000000", true)}
                        />
                    </Bar>
                    <Bar dataKey="alertCount" stackId="a" fill="#F43860" barSize={barWidth} >
                        {chartData.map((entry, index) => (
                            <Cell
                                key={`alert-${index}`}
                                radius={entry.originalAccessCount === 0 ? [10, 10, 10, 10] : [10, 10, 0, 0]}
                            />
                        ))}
                        <LabelList
                            dataKey="originalAlertCount"
                            content={CustomBarLabel("#ffffff")}
                        />
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
}
